import * as React from 'react';
import { Table, TableBody, TableContainer } from '@mui/material';

import pluralize from 'pluralize';
import { ColumnMappingHeader } from './ColumnMappingHeader';
import { ColumnMappingRow } from './ColumnMappingRow';
import { UploadedFileColumn } from './types';

interface Props {
  columns: UploadedFileColumn[];
  /** Called when the user changes any of the columns; they must all be updated atomically */
  setColumns: (columns: UploadedFileColumn[]) => void;
}

/**
 * Renders a table of column names found within a customer's uploaded file and
 * allows the user to map these columns to BlueCargo properties.
 */
export const ColumnMappingTable: React.FC<Props> = (props) => {
  const { columns, setColumns } = props;

  // Find out if we have a column mapped to the customer id
  const customerMappedColumn = React.useMemo(() => {
    return columns.find((c) => c.mapping === 'customer');
  }, [columns]);

  // We'll show the full table from the start if there is no column mapped to customer id
  const [showFullTable, setShowFullTable] = React.useState(!customerMappedColumn);

  // On mount, we need to determine which columns to show in the summary view; this must remain the same
  // even if the user changes the columns such that they no longer meet the summary filter criteria
  // hence the empty dependency array (only run on mount)
  const summaryColumnIds = React.useMemo(() => {
    return columns.filter((c) => c.mapping !== null).map((c) => c.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // However we always want the summary columns to be up to date, so this function uses the above list
  // to filter by.
  const summaryColumns = React.useMemo(() => {
    return columns.filter((c) => summaryColumnIds.includes(c.id));
  }, [summaryColumnIds, columns]);

  // When a mapping is updated, we need to update the entire column list atomically since assigning one
  // mapping may require unassigning another
  const handleUpdateColumn = (column: UploadedFileColumn) => {
    const modified = columns.map((c) => {
      // Update the specified item
      if (c.id === column.id) {
        return { ...column };
      }

      // Nullify the mapping of any other columns that used the same mapping
      if (c.mapping === column.mapping) {
        return { ...c, mapping: null };
      }

      // Pass through all other columns
      return { ...c };
    });

    setColumns(modified);
  };

  const rows = (showFullTable ? columns : summaryColumns).map((c) => (
    <ColumnMappingRow key={c.id} column={c} onUpdateColumn={handleUpdateColumn} />
  ));

  const difference = columns.length - summaryColumns.length;

  return (
    <div className="space-y-4">
      <TableContainer>
        <Table className="table-fixed">
          <ColumnMappingHeader />
          <TableBody>{rows}</TableBody>
        </Table>
      </TableContainer>
      {!showFullTable && (
        <h5
          className="text-blue-900 cursor-pointer underline"
          onClick={() => setShowFullTable((v) => !v)}
        >
          Not the right column? Pick from the other {difference} {pluralize('column', difference)}{' '}
          detected in file
        </h5>
      )}
    </div>
  );
};
ColumnMappingTable.displayName = 'ColumnMappingTable';
